import { useState } from 'react';
import { Alert, Box, Button, FileInput, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type Props = {
  onPreviewDownload: () => Promise<void>;
  isPreviewDownloading: boolean;
  onDeployedDownload: () => Promise<void>;
  isDeployedDownloading: boolean;
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  onParseDocx: (file: File) => Promise<void>;
  isParsing: boolean;
};

export default function DownloadUploadSection({
  onPreviewDownload,
  isPreviewDownloading,
  onDeployedDownload,
  isDeployedDownloading,
  onUpload,
  isUploading,
  onParseDocx,
  isParsing,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [parseFile, setParseFile] = useState<File | null>(null);
  const theme = useMantineTheme();

  const handleUpload = async () => {
    if (!file) return;
    try {
      await onUpload(file);
      notifications.show({
        title: 'Uploaded',
        message: 'export_assets.docx has been uploaded and will override the generated file.',
        color: 'green',
      });
      setFile(null);
    } catch {
      notifications.show({
        title: 'Upload failed',
        message: 'Failed to upload the file.',
        color: 'red',
      });
    }
  };

  const handleParse = async () => {
    if (!parseFile) return;
    try {
      await onParseDocx(parseFile);
      notifications.show({
        title: 'Parsed',
        message: 'Branding configuration has been extracted from the uploaded file.',
        color: 'green',
      });
      setParseFile(null);
    } catch {
      notifications.show({
        title: 'Parse failed',
        message: 'Failed to parse branding from the file.',
        color: 'red',
      });
    }
  };

  const containerStyle = {
    borderRadius: 16,
    border: `1px solid ${theme.colors.gray[2]}`,
    overflow: 'hidden' as const,
  };

  return (
    <Stack gap="md">
      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Download Preview</Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'normal' }}>
            Generates a fresh export_assets.docx from your saved configuration so you can inspect it
            in Word. This does not deploy the file.
          </Text>
          <Button
            onClick={onPreviewDownload}
            loading={isPreviewDownloading}
            style={{ alignSelf: 'flex-start' }}
          >
            Download Preview
          </Button>
        </Stack>
      </Box>

      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Download Deployed File</Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'normal' }}>
            Downloads the currently deployed export_assets.docx from S3 (the file that is actually
            used for document exports).
          </Text>
          <Button
            onClick={onDeployedDownload}
            loading={isDeployedDownloading}
            variant="light"
            style={{ alignSelf: 'flex-start' }}
          >
            Download Deployed File
          </Button>
        </Stack>
      </Box>

      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Manual Upload Override</Text>
          <Alert color="yellow" variant="light">
            Uploading a file manually will override the deployed export_assets.docx. The saved
            configuration above will not reflect the manually uploaded file.
          </Alert>
          <FileInput
            label="Upload export_assets.docx"
            placeholder="Select .docx file"
            accept=".docx"
            value={file}
            onChange={setFile}
          />
          <Group>
            <Button
              onClick={handleUpload}
              loading={isUploading}
              disabled={!file}
              variant="light"
              color="orange"
            >
              Upload Override
            </Button>
          </Group>
        </Stack>
      </Box>

      <Box p="md" style={containerStyle}>
        <Stack gap="sm">
          <Text fw={FONT_WEIGHTS.semibold}>Upload & Parse Branding (Test)</Text>
          <Alert color="blue" variant="light">
            Upload an existing .docx file to extract its styles and back-populate the branding
            configuration. This will overwrite the current branding config.
          </Alert>
          <FileInput
            label="Upload .docx to parse"
            placeholder="Select .docx file"
            accept=".docx"
            value={parseFile}
            onChange={setParseFile}
          />
          <Group>
            <Button
              onClick={handleParse}
              loading={isParsing}
              disabled={!parseFile}
              variant="light"
              color="blue"
            >
              Upload & Parse
            </Button>
          </Group>
        </Stack>
      </Box>
    </Stack>
  );
}
